// Decides which MFA enrollment buttons to show based on the company's
// `settings.supported_authenticators` (from the authenticated /company/ endpoint).
//
// supported: array | null | undefined
//   - null      => company supports all authenticators
//   - array     => show only the listed types (`static` has no enrollment button)
//   - undefined => no data (legacy company / not loaded yet) -> show all by default
//
// hideSmsMfa: legacy white-label app-config flag. It is applied as an OVERRIDE on
// top of the company config (backwards compatible): if a company already disabled
// SMS via app config, SMS stays hidden regardless of supported_authenticators.
// There is no equivalent app-config flag for TOTP, so only SMS is overridden.
export function getMfaAuthenticatorVisibility(supported, hideSmsMfa) {
  let showTotp;
  let showSms;

  if (supported === null) {
    showTotp = true;
    showSms = true;
  } else if (Array.isArray(supported) && supported.length > 0) {
    showTotp = supported.includes('totp');
    showSms = supported.includes('sms');
  } else {
    // undefined / empty => no company data, default to showing all
    showTotp = true;
    showSms = true;
  }

  // App config overrides the company config.
  if (hideSmsMfa) {
    showSms = false;
  }

  return { showTotp, showSms };
}

// Returns the single available authenticator ('totp' | 'sms') when exactly one
// method is visible, otherwise null (0 or 2+ methods -> keep the selection screen).
export function getSoleMfaAuthenticator(supported, hideSmsMfa) {
  const { showTotp, showSms } = getMfaAuthenticatorVisibility(supported, hideSmsMfa);
  if (showTotp && !showSms) return 'totp';
  if (showSms && !showTotp) return 'sms';
  return null;
}

// The API's throttle message ("Request was throttled: ... Expected available
// in 3317 seconds.") is too raw to show users; reword it with a rounded wait.
// Other messages pass through unchanged. `t` is an i18next translate function.
export function formatMfaError(message = '', t) {
  if (!/throttled/i.test(message)) return message;
  const seconds = Number(message.match(/(\d+)\s*seconds/i)?.[1]);
  const minutes = seconds ? Math.max(1, Math.round(seconds / 60)) : 0;
  return minutes
    ? t('mfa_throttled_wait_error', {
        defaultValue:
          'Too many attempts. Please try again in about {{minutes}} min.',
        minutes,
      })
    : t('mfa_throttled_error', {
        defaultValue: 'Too many attempts. Please try again later.',
      });
}
